/**
 * Combat math — pure functions implementing BATTLES.md §2.7.8.1-.7 (v0M3.3).
 *
 * All numeric constants match §2.7.8.11 placeholder dials — those
 * migrate to `Balance.xlsx` per the data-authoring principle at
 * §2.7.8.11 preamble. Sourced from the doc verbatim, referenced by
 * inline citations.
 *
 * Determinism (§2.7.8.13 rule c): every RNG call goes through the
 * battle's PRNG threaded via arguments. No global state.
 *
 * This module is PURE where possible — most functions take state,
 * compute, and return a result / apply mutations to the passed cohort.
 * The round loop (`battle-round.ts`) orchestrates the calls.
 */

import type { RngState } from './rng';
import { chance, nextInt } from './rng';
import type { AttackAngle } from './cohort';
import {
  aliveFigureCount,
  chebyshevDistance,
  classifyAngleFromSquares,
  compactFigures,
  hasChargeResistance,
  healthyFrontRowCount,
  isCombatAdjacent,
  isMelee,
  isMoraleAdjacent,
  isRanged,
  netCardinalDirection,
  turnToFaceFirstAttacker,
} from './cohort';
import type { CohortOnField } from './battle-state';

// ---------------------------------------------------------------------------
// Balance dials (mirror §2.7.8.11)
// ---------------------------------------------------------------------------

/** Base melee to-hit % — §2.7.8.1.b sub-phase 2. */
export const BASE_MELEE_TO_HIT = 0.5;
/** Base missile to-hit % — §2.7.8.2.c sub-phase 2. */
export const BASE_MISSILE_TO_HIT = 0.3;
/** To-hit clamp minimum — §2.7.8.1 + §2.7.8.2. */
export const TO_HIT_MIN = 0.05;
/** To-hit clamp maximum — §2.7.8.1 + §2.7.8.2. */
export const TO_HIT_MAX = 0.95;
/** Wounded threshold as fraction of max health — §2.7.8.1.b sub-phase 4. */
export const WOUNDED_THRESHOLD = 0.5;
/** Missile supply drain per firing round — §2.7.8.2. */
export const MISSILE_SUPPLY_DRAIN_PER_FIRE = 1;

/** Charge modifiers (§2.7.8.3.b). */
export const CHARGE_TO_HIT_BONUS = 0.1;
export const CHARGE_DEFENDER_MORALE_HIT = -10;
export const CHARGE_DEFENDER_STAMINA_HIT = -10;
export const CHARGE_CHARGER_STAMINA_COST = -5;
export const CHARGE_STAMINA_FLOOR = 30;

/** Flank + rear modifiers (§2.7.8.4.d-.e). */
export const FLANK_DEFENDER_MORALE_HIT = -10;
export const FLANK_DEFENDER_STAMINA_HIT = -10;
export const REAR_DEFENDER_MORALE_HIT = -20;
export const REAR_DEFENDER_STAMINA_HIT = -20;

/** Per-strike stamina drain (§2.7.8.5.a). */
export const ATTACKER_STAMINA_DRAIN_PER_STRIKE = -2;
export const DEFENDER_STAMINA_DRAIN_PER_STRIKE = -1;
export const IDLE_STAMINA_RECOVERY = 1;

/** Rout adjacency morale hit (§2.7.8.6.c-.d). */
export const ADJACENT_ALLY_ROUT_MORALE_HIT = -10;
export const ADJACENT_ENEMY_ROUT_MORALE_GAIN = 10;

/** Starting stamina/morale per §2.7.8.11. */
export const STAMINA_START = 100;
export const MORALE_START = 100;

// ---------------------------------------------------------------------------
// Stamina + morale helpers (bounded [0, START])
// ---------------------------------------------------------------------------

export function applyStamina(cohort: CohortOnField, delta: number): void {
  cohort.stamina = Math.max(0, Math.min(STAMINA_START, cohort.stamina + delta));
}

export function applyMorale(cohort: CohortOnField, delta: number): void {
  cohort.morale = Math.max(0, Math.min(MORALE_START, cohort.morale + delta));
}

// ---------------------------------------------------------------------------
// Rout trigger + rout-propagation (§2.7.8.5.c + §2.7.8.6.c-.d)
// ---------------------------------------------------------------------------

/** True if the cohort should be marked broken per §2.7.8.5.c. */
export function shouldRout(cohort: CohortOnField): boolean {
  return !cohort.broken && (cohort.stamina <= 0 || cohort.morale <= 0);
}

/**
 * Propagate rout-morale effects to 8-adjacent cohorts.
 *  - Allies (same side): −10 morale each.
 *  - Enemies (opposite side): +10 morale each.
 *
 * Returns the list of cohorts that themselves rout as a result (cascade).
 * §2.7.8.6.c-.d + §2.7.8.6.f cascade behaviour.
 */
export function propagateRoutMorale(
  routedCohort: CohortOnField,
  allSideACohorts: readonly CohortOnField[],
  allSideBCohorts: readonly CohortOnField[],
): CohortOnField[] {
  const alliedSide = allSideACohorts.includes(routedCohort) ? allSideACohorts : allSideBCohorts;
  const enemySide = alliedSide === allSideACohorts ? allSideBCohorts : allSideACohorts;

  const newlyRouted: CohortOnField[] = [];

  for (const ally of alliedSide) {
    if (ally === routedCohort) continue;
    if (ally.broken) continue;
    if (!isMoraleAdjacent(routedCohort.position, ally.position)) continue;
    applyMorale(ally, ADJACENT_ALLY_ROUT_MORALE_HIT);
    if (shouldRout(ally)) {
      ally.broken = true;
      newlyRouted.push(ally);
    }
  }

  for (const enemy of enemySide) {
    if (enemy.broken) continue;
    if (!isMoraleAdjacent(routedCohort.position, enemy.position)) continue;
    applyMorale(enemy, ADJACENT_ENEMY_ROUT_MORALE_GAIN);
    // Note: enemies GAINING morale on our rout can't trigger their own rout.
  }

  return newlyRouted;
}

// ---------------------------------------------------------------------------
// Charge trigger + modifiers (§2.7.8.3)
// ---------------------------------------------------------------------------

export interface ChargeState {
  readonly triggered: boolean;
  readonly resistedByDefender: boolean;
}

/**
 * Check charge trigger per §2.7.8.3.a. Returns whether charge fires
 * (four conditions) and whether the defender resisted (§2.7.8.3.c).
 *
 * If the charge fires and is NOT resisted, the caller applies §2.7.8.3.b
 * modifiers: attack bonus, +10% to-hit, −10 defender morale, −10 defender
 * stamina at start of strike; −5 charger stamina post-strike.
 */
export function checkChargeTrigger(
  attacker: CohortOnField,
  defender: CohortOnField,
): ChargeState {
  // Condition 1: chargeBonus > 0.
  if (attacker.stats.chargeBonus <= 0) return { triggered: false, resistedByDefender: false };
  // Condition 2: stamina > 30.
  if (attacker.stamina <= CHARGE_STAMINA_FLOOR) return { triggered: false, resistedByDefender: false };
  // Condition 3: moved ≥1 square this round.
  if (!attacker.movedThisRound) return { triggered: false, resistedByDefender: false };
  // Condition 4: attack direction = movement direction.
  const attackDir = netCardinalDirection(attacker.position, defender.position);
  if (attackDir === null) return { triggered: false, resistedByDefender: false };
  if (attackDir !== attacker.moveDirection) return { triggered: false, resistedByDefender: false };

  // Trigger fired. Charge Resistance ability negates all modifiers.
  const resisted = hasChargeResistance(defender);
  return { triggered: true, resistedByDefender: resisted };
}

// ---------------------------------------------------------------------------
// Strike parameters (§2.7.8.1.b sub-phase 2-3 with charge/flank/rear mods)
// ---------------------------------------------------------------------------

export interface StrikeParams {
  toHit: number;
  attackerAttack: number;
  attackerStamina: number;
  defenderDefence: number;
  defenderStamina: number;
  defenderArmor: number;
  angle: AttackAngle;
  chargeActive: boolean;
}

/**
 * Compute strike parameters after applying all modifiers (charge + flank
 * + rear), clamping to-hit into [5%, 95%], BEFORE the strike sub-phases
 * consume them.
 *
 * §2.7.8.3.d + §2.7.8.4.g sequencing rules:
 *  - Charge-triggered modifiers apply first (attack + chargeBonus, +10% to-hit,
 *    −10 defender morale + stamina at start of strike).
 *  - Flank/rear apply at start of strike: defence removed from formula,
 *    −10 or −20 defender morale + stamina.
 *  - Terrain modifiers (open item 9) — flat 0 for v0M3.3 first pass.
 *
 * The passed cohorts have their morale/stamina applied here (not via
 * separate call) for the charge/flank/rear defender-side hits. Charger
 * stamina cost applies AFTER strike resolution (see applyPostStrikeCharger).
 */
export function computeStrikeParams(
  attacker: CohortOnField,
  defender: CohortOnField,
  baseToHit: number,
  terrainToHitDelta: number = 0,
): StrikeParams {
  const angle = classifyAngleFromSquares(defender, attacker);

  // Charge check — attacker-side (only for adjacent melee strikes).
  const chargeState = isCombatAdjacent(attacker.position, defender.position)
    ? checkChargeTrigger(attacker, defender)
    : { triggered: false, resistedByDefender: false };

  const chargeActive = chargeState.triggered && !chargeState.resistedByDefender;

  // Apply defender-side hits BEFORE the strike (see §2.7.8.3.b items 3-4
  // and §2.7.8.4.d-.e — all defender morale/stamina drops at strike start).
  if (chargeActive) {
    applyMorale(defender, CHARGE_DEFENDER_MORALE_HIT);
    applyStamina(defender, CHARGE_DEFENDER_STAMINA_HIT);
  }
  if (angle === 'flank') {
    applyMorale(defender, FLANK_DEFENDER_MORALE_HIT);
    applyStamina(defender, FLANK_DEFENDER_STAMINA_HIT);
  } else if (angle === 'rear') {
    applyMorale(defender, REAR_DEFENDER_MORALE_HIT);
    applyStamina(defender, REAR_DEFENDER_STAMINA_HIT);
  }

  const attackerAttack = attacker.stats.attack + (chargeActive ? attacker.stats.chargeBonus : 0);
  const toHitBase = baseToHit + (chargeActive ? CHARGE_TO_HIT_BONUS : 0);
  // v0M4.0k.4 (2026-07-27) — Terrain-based to-hit modifier is now
  // an ADDITIVE percentage-point delta from Combat.xlsx (was:
  // multiplicative in v0M4.0j.3). Sourced via `meleeTerrainToHitDelta`
  // in combat-terrain.ts and passed by the caller. Default 0 (no
  // effect) preserves backward compatibility with pre-v0M4.0k fixtures.
  const toHitAfterTerrain = toHitBase + terrainToHitDelta;
  const toHit = Math.max(TO_HIT_MIN, Math.min(TO_HIT_MAX, toHitAfterTerrain));

  return {
    toHit,
    attackerAttack,
    attackerStamina: attacker.stamina,
    // Flank/rear: defence removed entirely from damage formula.
    defenderDefence: angle === 'front' ? defender.stats.defence : 0,
    defenderStamina: defender.stamina,
    defenderArmor: defender.stats.armor,
    angle,
    chargeActive,
  };
}

/**
 * Compute damage for a single successful hit per §2.7.8.1.b sub-phase 3:
 *
 *   attackStrength = round((attack) × stamina / 100)
 *   defenceStrength = round(defence × stamina / 100)
 *   damage = max(0, attackStrength − armor − defenceStrength)
 */
export function computeDamagePerHit(params: StrikeParams): number {
  const attackStrength = Math.round((params.attackerAttack * params.attackerStamina) / 100);
  const defenceStrength = Math.round((params.defenderDefence * params.defenderStamina) / 100);
  return Math.max(0, attackStrength - params.defenderArmor - defenceStrength);
}

// ---------------------------------------------------------------------------
// Sub-phase 1: melee target selection (§2.7.8.1.b sub-phase 1)
// ---------------------------------------------------------------------------

/**
 * Assign each attacker front-row figure a target defender front-row
 * figure. Returns an array where index[i] = defender-index for attacker i.
 *
 * Rules per §2.7.8.1.b sub-phase 1:
 *  - Equal front-row counts N==M: 1:1 by column (attacker i targets
 *    defender i).
 *  - N > M: 2M − N centre defenders 1:1; outer (N − M) defenders each
 *    get a second attacker from outermost unmatched attacker positions.
 *  - N < M: 1:1 to first N defenders; extras (M − N) untargeted.
 */
export function selectMeleeTargets(
  attackerFrontRow: number,
  defenderFrontRow: number,
): (number | null)[] {
  const N = attackerFrontRow;
  const M = defenderFrontRow;
  const assignments: (number | null)[] = new Array(N).fill(null);

  if (N === 0 || M === 0) return assignments;

  if (N <= M) {
    // Each attacker targets a defender 1:1; extra defenders untargeted.
    for (let i = 0; i < N; i++) assignments[i] = i;
    return assignments;
  }

  // N > M — 2:1 focus assignment.
  // 2M − N centre columns 1:1; N − M outer columns each pair up 2:1.
  const oneToOne = 2 * M - N;
  const focusCount = N - M;
  if (oneToOne >= 0) {
    // Centre 1:1: attacker columns [focusCount/2, focusCount/2 + oneToOne)
    // target defenders [focusCount/2, focusCount/2 + oneToOne). Wait — this
    // doesn't work when the offsets are odd. Simpler rule that produces
    // deterministic results: iterate attackers left-to-right, assign
    // 2-per-defender to the outer defenders first, then 1:1 to the rest.
  }

  // Simple deterministic rule: assign 2-per-defender to the outermost
  // defenders (they're the exposed edges); centre defenders take 1
  // attacker each.
  //  - Outer half of the outer columns each get a 2nd attacker.
  //  - Centre defenders each get 1 attacker.
  //
  // Given N attackers and M defenders with N > M:
  //   The (N − M) "extra" attackers pile onto defenders 0..focusCount-1
  //   (in matched-pair positions from the outer edges). But to keep it
  //   deterministic and simple: extras go to defender columns [0, focusCount).
  const attackerIdx: number[] = [];
  for (let i = 0; i < N; i++) attackerIdx.push(i);

  for (let d = 0; d < M; d++) {
    if (d < focusCount) {
      // Focus defender — takes 2 attackers.
      const a1 = attackerIdx.shift();
      const a2 = attackerIdx.pop();
      if (a1 !== undefined) assignments[a1] = d;
      if (a2 !== undefined) assignments[a2] = d;
    } else {
      // 1:1 centre defender.
      const a = attackerIdx.shift();
      if (a !== undefined) assignments[a] = d;
    }
  }

  return assignments;
}

// ---------------------------------------------------------------------------
// Full melee strike (§2.7.8.1.b sub-phases 1-4)
// ---------------------------------------------------------------------------

export interface StrikeResult {
  hitCount: number;
  killCount: number;
  woundedCount: number;
  totalDamageDealt: number;
  chargeActive: boolean;
  angle: AttackAngle;
}

/**
 * Execute a single melee strike from `attacker` to `defender`.
 * Implements §2.7.8.1.b sub-phases 1-4 with charge/flank/rear
 * modifiers via computeStrikeParams:
 *  1. Target selection (sub-phase 1) — selectMeleeTargets.
 *  2. To-hit rolls (sub-phase 2) — one per attacker figure with a target.
 *  3. Damage calc (sub-phase 3) — computed per hit, ACCUMULATED per
 *     defender figure before application (atomic damage per Michael's
 *     spec).
 *  4. Damage application (sub-phase 4) — apply sum per figure; determine
 *     killed/wounded; compact roster.
 * Post-strike: attacker −2 stamina, defender −1 stamina, morale updates
 * for kills applied by the caller (advance-round.ts) since kill counts
 * from all strikes this round accumulate.
 */
export function resolveMeleeStrike(
  attacker: CohortOnField,
  defender: CohortOnField,
  rng: RngState,
  terrainToHitDelta: number = 0,
): StrikeResult {
  const params = computeStrikeParams(attacker, defender, BASE_MELEE_TO_HIT, terrainToHitDelta);
  const attackerFront = healthyFrontRowCount(attacker);
  const defenderFront = healthyFrontRowCount(defender);
  const assignments = selectMeleeTargets(attackerFront, defenderFront);

  // Sub-phase 2: to-hit rolls (attacker column order).
  // Sub-phase 3: damage per hit, accumulated per defender figure.
  const damagePerDefenderFigure = new Array<number>(defenderFront).fill(0);
  let hitCount = 0;
  for (let a = 0; a < assignments.length; a++) {
    const targetIdx = assignments[a];
    if (targetIdx === null || targetIdx === undefined) continue;
    const hit = chance(rng, params.toHit);
    if (!hit) continue;
    hitCount++;
    const dmg = computeDamagePerHit(params);
    damagePerDefenderFigure[targetIdx] = (damagePerDefenderFigure[targetIdx] ?? 0) + dmg;
  }

  // Sub-phase 4: apply damage atomically per figure.
  let killCount = 0;
  let woundedCount = 0;
  const maxHealth = defender.stats.maxHealthPerFigure;
  for (let i = 0; i < defenderFront; i++) {
    const damage = damagePerDefenderFigure[i]!;
    if (damage === 0) continue;
    const figure = defender.figures[i]!;
    if (figure.killed) continue;
    figure.health -= damage;
    if (figure.health <= 0) {
      figure.killed = true;
      killCount++;
    } else if (figure.health < WOUNDED_THRESHOLD * maxHealth) {
      figure.wounded = true;
      woundedCount++;
    }
  }

  // Compact the roster (promote back-row healthy figures to fill vacated
  // front-row slots per §2.7.8.1.b sub-phase 4).
  if (killCount > 0 || woundedCount > 0) {
    compactFigures(defender);
  }

  // Flag round bookkeeping.
  attacker.attackedThisRound = true;
  defender.struckThisRound = true;

  return {
    hitCount,
    killCount,
    woundedCount,
    totalDamageDealt: damagePerDefenderFigure.reduce((a, v) => a + v, 0),
    chargeActive: params.chargeActive,
    angle: params.angle,
  };
}

// ---------------------------------------------------------------------------
// Missile fire (§2.7.8.2.c)
// ---------------------------------------------------------------------------

export interface MissileResult {
  fired: boolean;
  hitCount: number;
  killCount: number;
  woundedCount: number;
  friendlyFireHits: number;
  friendlyFireKills: number;
}

/**
 * Execute missile fire from `firer` at `targetCohort`. Combined-pool
 * friendly fire handled: if `engagedFriendlyCohorts` is non-empty,
 * each firing figure draws a target uniformly from
 * (target figures + Σ engaged-friend figures).
 *
 * Returns false in `fired` if the cohort couldn't fire (see the check
 * order below).
 *
 * §2.7.8.2.b firing constraints:
 *  1. Standing order = Attack.
 *  2. Missile Supply > 0.
 *  3. No enemy cohort adjacent (caller's responsibility to pass a firer
 *     only when this holds — this function assumes the caller checked).
 *  4. Target within Missile Attack Range (Chebyshev).
 */
export function resolveMissileFire(
  firer: CohortOnField,
  targetCohort: CohortOnField,
  engagedFriendlyCohorts: readonly CohortOnField[],
  rng: RngState,
  terrainToHitDelta: number = 0,
): MissileResult {
  const zero: MissileResult = {
    fired: false, hitCount: 0, killCount: 0, woundedCount: 0,
    friendlyFireHits: 0, friendlyFireKills: 0,
  };

  // v0M4.0b.3: ranged-order gate. Only fire-at-will + aggressive-fire fire;
  // hold-fire + everything else (melee orders, etc.) skip. Missile Supply
  // preserved for the non-firing orders.
  if (firer.standingOrder !== 'fire-at-will' && firer.standingOrder !== 'aggressive-fire') {
    return zero;
  }
  if (firer.missileSupply <= 0) return zero;
  if (chebyshevDistance(firer.position, targetCohort.position) > firer.stats.missileRange) return zero;

  // Build the target pool. Under Fire at Will the caller already skipped
  // enemies engaged with friendlies (via chooseMissileTarget), so no
  // friendlies should ever be in the pool — safe to exclude them here
  // unconditionally as a defensive check.
  // Under Aggressive Fire the caller passes engagedFriendlies, and this
  // function includes them in the pool per §2.7.8.2 combined-pool math.
  interface PoolEntry { cohort: CohortOnField; figureIdx: number; }
  const pool: PoolEntry[] = [];
  for (let i = 0; i < targetCohort.figures.length; i++) {
    if (!targetCohort.figures[i]!.killed) pool.push({ cohort: targetCohort, figureIdx: i });
  }
  if (firer.standingOrder === 'aggressive-fire') {
    for (const friend of engagedFriendlyCohorts) {
      for (let i = 0; i < friend.figures.length; i++) {
        if (!friend.figures[i]!.killed) pool.push({ cohort: friend, figureIdx: i });
      }
    }
  }
  if (pool.length === 0) return zero;

  // §2.7.8.2.a: every figure in the firing cohort fires (not just front row).
  const firerFigures = firer.figures.filter((f) => !f.killed).length;

  // Group damage by figure so atomic damage application works.
  // Map keyed by "cohortId:figureIdx" → accumulated damage.
  const damageAcc = new Map<string, number>();
  const cohortsHit = new Set<CohortOnField>();

  let hitCount = 0;
  let friendlyFireHits = 0;
  for (let i = 0; i < firerFigures; i++) {
    // Target draw (deterministic via rng).
    const entry = pool[nextInt(rng, 0, pool.length)]!;
    // To-hit roll per §2.7.8.2.c sub-phase 2 — 30% base + terrain delta
    // (v0M4.0k.4, BATTLES.md §2.8.6.5; additive replaces multiplicative),
    // clamped to [TO_HIT_MIN, TO_HIT_MAX].
    const toHit = Math.max(
      TO_HIT_MIN,
      Math.min(TO_HIT_MAX, BASE_MISSILE_TO_HIT + terrainToHitDelta),
    );
    const hit = chance(rng, toHit);
    if (!hit) continue;
    hitCount++;
    if (entry.cohort !== targetCohort) friendlyFireHits++;

    // Damage — uses target-figure's cohort stats (whichever cohort the figure belongs to).
    const cohort = entry.cohort;
    const attackStrength = Math.round((firer.stats.missileAttack * firer.stamina) / 100);
    const defenceStrength = Math.round((cohort.stats.defence * cohort.stamina) / 100);
    const damage = Math.max(0, attackStrength - cohort.stats.armor - defenceStrength);

    const key = `${cohort.id}:${entry.figureIdx}`;
    damageAcc.set(key, (damageAcc.get(key) ?? 0) + damage);
    cohortsHit.add(cohort);
  }

  // Sub-phase 4: apply damage atomically per figure.
  let killCount = 0;
  let woundedCount = 0;
  let friendlyFireKills = 0;
  for (const [key, damage] of damageAcc) {
    if (damage === 0) continue;
    const [cohortIdStr, figureIdxStr] = key.split(':');
    const figureIdx = Number(figureIdxStr);
    // Find the cohort in cohortsHit (should be one match on id).
    const cohort = [...cohortsHit].find((c) => String(c.id) === cohortIdStr)!;
    const figure = cohort.figures[figureIdx]!;
    if (figure.killed) continue;
    figure.health -= damage;
    if (figure.health <= 0) {
      figure.killed = true;
      killCount++;
      if (cohort !== targetCohort) friendlyFireKills++;
    } else if (figure.health < WOUNDED_THRESHOLD * cohort.stats.maxHealthPerFigure) {
      figure.wounded = true;
      woundedCount++;
    }
  }

  // Compact rosters of every cohort hit.
  for (const c of cohortsHit) compactFigures(c);

  // Missile supply drain — 1 per firing round (§2.7.8.2 v0M1.9.10 clarification).
  firer.missileSupply -= MISSILE_SUPPLY_DRAIN_PER_FIRE;

  // Flag round bookkeeping.
  firer.attackedThisRound = true;
  targetCohort.struckThisRound = true;
  for (const c of cohortsHit) c.struckThisRound = true;

  return {
    fired: true,
    hitCount,
    killCount,
    woundedCount,
    friendlyFireHits,
    friendlyFireKills,
  };
}

// ---------------------------------------------------------------------------
// Kill morale attribution — §2.7.8.6.a-.b (v0M3.3 uses simple attribution)
// ---------------------------------------------------------------------------

/**
 * Apply casualty morale for a strike outcome per §2.7.8.6.a-.b.
 *
 * Session-28 (2026-07-24) — Michael's design change:
 *  - Attacker: +1 morale per enemy figure killed (unchanged).
 *  - Defender: −1 morale per own figure wounded + −3 morale per own
 *    figure killed. Was −1 per kill only. Wounds now bite. Rationale:
 *    closes the "cohort sits with all-red figures at 100 morale
 *    indefinitely" gap surfaced in session-27 playtest — heavy wounds
 *    now drive morale down even when strikes fail to kill.
 *
 * Multi-attacker kill attribution (open item 26) uses simple attribution:
 * attacker of THIS strike gets full credit for kills + wounds in THIS
 * strike. Multi-cohort focus fire is handled at the round level (each
 * cohort's strike attributes its own casualties).
 *
 * Zero-casualty strikes are a no-op (guarded).
 */
export function applyKillMorale(
  attacker: CohortOnField,
  defender: CohortOnField,
  killCount: number,
  woundedCount: number,
): void {
  if (killCount === 0 && woundedCount === 0) return;
  applyMorale(attacker, killCount);
  applyMorale(defender, -(woundedCount + 3 * killCount));
}

// ---------------------------------------------------------------------------
// Idle stamina recovery (§2.7.8.5.b)
// ---------------------------------------------------------------------------

/**
 * Apply idle stamina recovery to a cohort per §2.7.8.5.b:
 *  - Did not move (§2.7.3 Movement phase).
 *  - Did not attack (no melee strike this round; no missile fire).
 *  - Did not defend (not struck this round).
 * → +1 stamina at end of round.
 */
export function applyIdleRecoveryIfEligible(cohort: CohortOnField): void {
  if (cohort.broken) return;
  if (cohort.movedThisRound) return;
  if (cohort.attackedThisRound) return;
  if (cohort.struckThisRound) return;
  applyStamina(cohort, IDLE_STAMINA_RECOVERY);
}

// ---------------------------------------------------------------------------
// Per-strike stamina drain (§2.7.8.5.a) — attacker −2, defender −1
// ---------------------------------------------------------------------------

/**
 * Apply the post-strike stamina cost per §2.7.8.5.a. Called AFTER
 * sub-phase 4 resolves so the strike itself uses pre-drain stamina in
 * the damage formula.
 *
 * If chargeActive, also apply the charger's −5 charge stamina cost
 * per §2.7.8.3.b item 5.
 */
export function applyPostStrikeStamina(
  attacker: CohortOnField,
  defender: CohortOnField,
  chargeActive: boolean,
): void {
  applyStamina(attacker, ATTACKER_STAMINA_DRAIN_PER_STRIKE);
  applyStamina(defender, DEFENDER_STAMINA_DRAIN_PER_STRIKE);
  if (chargeActive) applyStamina(attacker, CHARGE_CHARGER_STAMINA_COST);
}

// ---------------------------------------------------------------------------
// Attacker-side facing turn (§2.7.8.4.b)
// ---------------------------------------------------------------------------

/**
 * Called at the start of each strike: if this is the first attacker this
 * round for the defender, turn the defender to face the attacker (§2.7.8.4.b).
 * Only meaningful for cardinally-adjacent attackers.
 */
export function updateDefenderFacing(defender: CohortOnField, attacker: CohortOnField): void {
  turnToFaceFirstAttacker(defender, attacker.position);
}

// Re-export helper types + underused imports (silence linters).
export type { AttackAngle };
export { aliveFigureCount, isMelee, isRanged, hasChargeResistance };
